import React, { useEffect, useMemo, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'

import { useCartRedux, useDashboardRedux, useUserRedux } from '@hooks'
import { DINING_METHOD, GST_STALL_LIST, INVENTORY_STATUS, PRODUCT_SELECTION_TYPE } from '@common'

import ProductVariantSelector from '../ProductVariantSelector'
import Note from '../Note'

import SelectionBlock from './SelectionBlock'
import { calculateSelectionPrice, validateSelections } from './utils'
import './ProductPopup.css'

const ProductPopup = ({ isOpen, onClose, product }) => {
    const { addItem } = useCartRedux()
    const { updatedInventoryStatus } = useDashboardRedux()
    const { diningMethod } = useUserRedux()
    const [selectedVariant, setSelectedVariant] = useState(null)
    const [selectedSelections, setSelectedSelections] = useState({})
    const [note, setNote] = useState('')
    const [quantity, setQuantity] = useState(1)

    const variantOptions = useMemo(
        () =>
            product?.variants.map(variant => ({
                value: variant.id,
                label: variant.name,
                sellPrice: variant.sellPrice,
                isDisabled:
                    updatedInventoryStatus[variant.inventory.id] === INVENTORY_STATUS.SOLD_OUT ||
                    variant.inventory?.status === INVENTORY_STATUS.SOLD_OUT,
                selections: variant.selections,
            })),
        [product?.variants, updatedInventoryStatus]
    )

    const selectionOptionsMap = useMemo(() => {
        const result = {}
        selectedVariant?.selections?.forEach(selection => {
            result[selection.id] = selection.options.map(option => ({
                value: option.id,
                label: option.name,
                sellPrice: option.sellPrice,
                quantity: 1,
                maxQuantity: option.maxQuantity,
                inventory: option.inventory,
                isDisabled: option.inventory?.status === INVENTORY_STATUS.SOLD_OUT,
                isTakeaway: selection.type === PRODUCT_SELECTION_TYPE.TAKEAWAY,
            }))
        })
        return result
    }, [selectedVariant?.selections])

    useEffect(() => {
        if (!product) return

        if (!selectedVariant && variantOptions.length > 0) {
            const activeVariant = variantOptions.find(v => !v.isDisabled)
            setSelectedVariant(activeVariant || variantOptions[0])
        }

        // Initialize selections state
        resetSelections()
    }, [product, variantOptions, selectedVariant])

    const resetSelections = () => {
        if (!selectedVariant?.selections) return

        const initialSelections = selectedVariant.selections.reduce((acc, selection) => {
            if (diningMethod.value === DINING_METHOD.Takeaway && selection.type === PRODUCT_SELECTION_TYPE.TAKEAWAY) {
                // handle takeaway
                const options = selectionOptionsMap[selection.id] || []
                const activeOption = options.find(o => !o.isDisabled)
                acc[selection.id] = options.length > 0 ? [activeOption || options[0]] : []
            } else {
                acc[selection.id] = selection.type === PRODUCT_SELECTION_TYPE.MULTIPLE ? [] : null
            }
            return acc
        }, {})

        setSelectedSelections(initialSelections)
    }

    const _renderProductVariantSelector = () => {
        return <ProductVariantSelector variants={variantOptions} value={selectedVariant} onChange={setSelectedVariant} />
    }

    const _renderSelections = () => {
        if (!selectedVariant?.selections) return null

        return selectedVariant.selections
            .filter(selection => {
                // if dining method is dine in, filter out take away options
                if (diningMethod.value === DINING_METHOD.DineIn) {
                    return selection.type !== PRODUCT_SELECTION_TYPE.TAKEAWAY
                }
                return true
            })
            .map(selection => (
                <SelectionBlock
                    key={selection.id}
                    selection={selection}
                    value={selectedSelections[selection.id]}
                    onChange={handleSelectionChange}
                    options={selectionOptionsMap[selection.id]}
                />
            ))
    }

    const _renderPrice = () => {
        const selectionPrice = calculateSelectionPrice(selectedVariant, selectedSelections)

        return <p className="product-price">${(selectedVariant?.sellPrice + selectionPrice).toFixed(2)}</p>
    }

    const _renderQuantityControl = () => {
        return (
            <div className="quantity-control">
                <i className={`bx bx-minus-circle icon ${quantity === 1 && 'disabled'}`} onClick={handleMinus}></i>
                <span className="quantity">{quantity}</span>
                <i className={`bx bx-plus-circle icon`} onClick={handlePlus}></i>
            </div>
        )
    }

    const _onClose = () => {
        setQuantity(1)
        setSelectedVariant(null)
        setSelectedSelections({})
        setNote('')
        onClose()
    }

    const handleSelectionChange = (selectionId, value) => {
        setSelectedSelections(prev => ({
            ...prev,
            [selectionId]: value,
        }))
    }

    const handleMinus = () => {
        if (quantity > 1) {
            setQuantity(prev => prev - 1)
        }
    }

    const handlePlus = () => {
        setQuantity(prev => prev + 1)
    }

    const handleAddToCart = () => {
        if (!validateSelections(selectedVariant, selectedSelections, diningMethod)) return

        const variant =
            product.variants.length > 1 ? product.variants.find(v => v.id === selectedVariant.value) : product.variants[0]

        // Transform selections to the required format
        const selections = selectedVariant?.selections
            .filter(selection => !!selectedSelections[selection.id])
            .map(selection => {
                const options = selectedSelections[selection.id]
                return {
                    id: selection.id,
                    name: selection.name,
                    quantity: selection.quantity,
                    options:
                        Array.isArray(options) ?
                            options.map(option => ({
                                id: option.value,
                                name: option.label,
                                quantity: option.quantity,
                                sellPrice: option.sellPrice,
                                inventory: option.inventory,
                            }))
                        :   [
                                {
                                    id: options.value,
                                    name: options.label,
                                    quantity: options.quantity,
                                    sellPrice: options.sellPrice,
                                    inventory: options.inventory,
                                },
                            ],
                }
            })

        const item = {
            cafeMenuId: product.id,
            cafeStallId: product.stallId,
            dishId: product.dishId,
            name: product.name,
            stallName: product.stallName,
            note: note,
            variants: [
                {
                    ...variant,
                    // inventory: undefined,
                    quantity,
                },
            ],
            selections,
        }

        addItem(item)
        _onClose()
    }

    if (!product) return null

    return (
        <Modal isOpen={isOpen} toggle={_onClose} size="lg" centered fullscreen>
            <ModalHeader className="product-modal-header" toggle={_onClose}>
                {product.name}
            </ModalHeader>
            <ModalBody>
                <div className="product-container">
                    {/* <img src={product.image} alt={product.name} className="product-image" /> */}
                    <div className="product-details">
                        {product.description && (
                            <p className="product-description" dangerouslySetInnerHTML={{ __html: product.description }} />
                        )}
                        {_renderProductVariantSelector()}
                        {_renderSelections()}
                        <Note value={note} onChange={setNote} containerClassName="note-container" />
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <div className="w-100">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                            {_renderQuantityControl()}
                            {_renderPrice()}
                        </div>
                        <Button color="primary" onClick={handleAddToCart}>
                            Add to Cart
                        </Button>
                    </div>
                    {GST_STALL_LIST.includes(product?.stallName) && (
                        <div className="d-flex justify-content-end mt-2">
                            <p className="text-secondary m-0" style={{ fontSize: '12px' }}>
                                (Inclusive of GST)
                            </p>
                        </div>
                    )}
                </div>
            </ModalFooter>
        </Modal>
    )
}

export default ProductPopup
